import SearchIcon from '@mui/icons-material/Search';
import { Card, Grid, Link } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { FieldValues, FormProvider, SubmitHandler, useFormContext } from 'react-hook-form';
import { BiFilterAlt } from 'react-icons/bi';
import { CgBot } from 'react-icons/cg';
import { useSearchParams } from 'react-router-dom';
import { csrfTokenCall, getNativeSearchAccountsForJobBoard } from 'wisestep-api';
import {
  SearchBar,
  TagsInput,
  WsButton,
  WsColors,
  WsLocationSearch,
  WsSelect,
  WsSelectObj,
  WsDrawer,
  ControlledTextInput,
  WsToggleButton,
  WsToggleObject,
  WsAutocompleteInput,
  // WsRadioGroup,
  WsRadioObj,
} from 'wisestep-base';

import WsSkillsFilter from '../../../components/filters/skillsFilter/WsSkillsFilter';
import NoDataFoundCard from '../../../components/noDataFoundCard';
import { useWorkspaceContext } from '../../../context';
import { firingAmplitudeEvents } from '../../../features/candidates/utils/CandidateHelper';
import { useJob } from '../../../features/jobs/hooks';
import useKeyWordGenerator, { Mode } from '../../../hooks/useKeyWordGenerator';
import { failed } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { useConversationWidgetStore } from '../../../utils/useConversationWidgetStore';
import HarvestBotsList from '../Bots/HarvestBotsListing';
import { States } from '../techfetch/utils/defaultAdvanceFilterValues';
import IBasicSearchForm from '../types/IBasicSearchForm';
import { toLowerCaseWithFirstLetterCapital, getJobBoardUrl } from '../utils/nativeSearchHelper';

import AddAccountButton from './AddAccountButton';
import HarvestBotDownloadMatchList from './HarvestBotDownloadMatchList';
import WsLocationAndZipSearch from './WsLocationAndZipSearch';

interface NativeSearchFormProps<T> {
  jobBoardName:
    | 'DICE_SEARCH'
    | 'TECH_FETCH'
    | 'CAREER_BUILDER'
    | 'MONSTER'
    | 'CW_JOBS'
    | 'CAREER_BUILDER_EDGE'
    | 'JOB_SITE'
    | 'JOB_SERVE'
    | 'CV_LIBRARY';
  performSearch: (values: IBasicSearchForm & T) => void;
  openAdvanceFilter?: Dispatch<SetStateAction<boolean>>;
  defaultOverrides?: Partial<IBasicSearchForm>;
  handleSourceBotBtnClick?: () => void;
  country?: string;
  onViewCvClick?: (candidateId: string, openNewTab: boolean) => Promise<void>;
}
interface IViewProps {
  accountId: string;
  accountUsage?: number;
  userName: string;
}

const lastActiveValues = {
  DICE_SEARCH: [
    { label: 'Select', value: '' },
    { label: '30 Days', value: '30' },
    { label: '60 Days', value: '60' },
    { label: '90 Days', value: '90' },
    { label: '180 Days', value: '180' },
    { label: '1 Year', value: '366' },
    { label: '2 Years', value: '731' },
    { label: '3 Years', value: '1096' },
    { label: '5 Years', value: '1829' },
    { label: '8 Years', value: '2922' },
  ],
  TECH_FETCH: [
    { label: 'AnyTime', value: '' },
    { label: 'Present', value: '1' },
    { label: '7 days', value: '7' },
    { label: '30 Days', value: '30' },
    { label: '60 Days', value: '60' },
    { label: '90 Days', value: '90' },
    { label: '180 Days', value: '180' },
    { label: '270 Days', value: '270' },
    { label: '1 Year', value: '365' },
  ],
  MONSTER: [
    { label: 'AnyTime', value: '' },
    { label: 'Present', value: '1440' },
    { label: '7 days', value: '10080' },
    { label: '30 Days', value: '43200' },
    { label: '60 Days', value: '86400' },
    { label: '90 Days', value: '129600' },
    { label: '180 Days', value: '259200' },
    { label: '270 Days', value: '388800' },
    { label: '1 Year', value: '525600' },
  ],
  CAREER_BUILDER: [
    { label: 'Present', value: '1' },
    { label: 'Since Yesterday', value: '-1' },
    { label: '7 days', value: '7' },
    { label: '30 Days', value: '30' },
    { label: '90 Days', value: '90' },
    { label: '180 Days', value: '180' },
    { label: '270 Days', value: '270' },
    { label: '1 Year', value: '365' },
    { label: 'All Resume', value: '999' },
  ],
  CAREER_BUILDER_EDGE: [
    { label: 'Last 1 Day', value: '[NOW-1DAY/DAY TO *]' },
    { label: 'Last 7 Days', value: '[NOW-7DAYS/DAY TO *]' },
    { label: 'Last 30 Days', value: '[NOW-30DAYS/DAY TO *]' },
    { label: 'Last 90 Days', value: '[NOW-90DAYS/DAY TO *]' },
    { label: 'Last 180 Days', value: '[NOW-180DAYS/DAY TO *]' },
    { label: 'Last 1 Year', value: '[NOW-1YEAR/DAY TO *]' },
    { label: 'Last 2 Years', value: '[NOW-2YEAR/DAY TO *]' },
    { label: 'Last 10 Years', value: '[NOW-10YEARS/DAY TO *]' },
  ],
  CW_JOBS: [
    { label: 'Select', value: '' },
    { label: '30 Days', value: '30' },
    { label: '60 Days', value: '60' },
    { label: '90 Days', value: '90' },
    { label: '180 Days', value: '180' },
    { label: '1 Year', value: '366' },
    { label: '2 Years', value: '731' },
    { label: '3 Years', value: '1096' },
    { label: '5 Years', value: '1829' },
    { label: '8 Years', value: '2922' },
  ],
  JOB_SITE: [
    { label: 'Select', value: '' },
    { label: '30 Days', value: '30' },
    { label: '60 Days', value: '60' },
    { label: '90 Days', value: '90' },
    { label: '180 Days', value: '180' },
    { label: '1 Year', value: '366' },
    { label: '2 Years', value: '731' },
    { label: '3 Years', value: '1096' },
    { label: '5 Years', value: '1829' },
    { label: '8 Years', value: '2922' },
  ],
  JOB_SERVE: [
    { label: 'Anytime', value: '' },
    { label: 'Today', value: 'Today' },
    { label: 'Within 1 Day', value: 'Within 1 Day' },
    { label: 'Within 3 Days', value: 'Within 3 Days' },
    { label: 'Within 1 Week', value: 'Within 1 Week' },
    { label: 'Within 1 Month', value: 'Within 1 Month' },
    { label: 'Within 3 Months', value: 'Within 3 Months' },
    { label: 'Within 6 Months', value: 'Within 6 Months' },
    { label: 'Within 1 Year', value: 'Within 1 Year' },
    { label: 'Within 2 Years', value: 'Within 2 Years' },
  ],
  CV_LIBRARY: [
    { label: 'Select', value: '' },
    { label: '30 Days', value: '30' },
    { label: '60 Days', value: '60' },
    { label: '90 Days', value: '90' },
    { label: '180 Days', value: '180' },
    { label: '1 Year', value: '366' },
    { label: '2 Years', value: '731' },
    { label: '3 Years', value: '1096' },
    { label: '5 Years', value: '1829' },
    { label: '8 Years', value: '2922' },
  ],
};

const sortOptions = {
  DICE_SEARCH: [
    { label: 'Relevance: Most', value: 'relevancy' },
    { label: 'Last Active: Most Recent', value: 'activityDate' },
    { label: 'Resume Updated: Most Recent', value: 'dateResumeLastUpdated' },
  ],
  TECH_FETCH: [
    { label: 'Relevance: Most', value: 'relevancy' },
    { label: 'Last Active: Most Recent', value: 'activityDate' },
    { label: 'Resume Updated: Most Recent', value: 'dateResumeLastUpdated' },
  ],
  MONSTER: [
    { label: 'Default', value: 'rank,mdate' },
    { label: 'Relevance: Most', value: 'rank' },
    { label: 'Last Active: Most Recent', value: 'mdate' },
    { label: 'Resume Created: Most Recent', value: 'cdate' },
    { label: 'Distance : Closest', value: 'distance' },
    { label: 'Resume Title : Alphabetical', value: 'title' },
    { label: 'Service Flag', value: 'mvf' },
    { label: 'Military involvement', value: 'minv' },
  ],
  CAREER_BUILDER: [
    { label: 'Relevance: Most', value: 'relevancy' },
    { label: 'Last Active: Most Recent', value: 'activityDate' },
    { label: 'Resume Updated: Most Recent', value: 'dateResumeLastUpdated' },
  ],
  CAREER_BUILDER_EDGE: [
    { label: 'Relevance: Most', value: 'relevancyMost' },
    { label: 'Relevance: Least', value: 'relevancyLeast' },
    { label: 'ProfileCompleteness: High to Low', value: 'profileMost' },
    { label: 'ProfileCompleteness: Low to High', value: 'profileLeast' },
    { label: 'SearchEngineRelevance: High to Low', value: 'engineMost' },
    { label: 'SearchEngineRelevance: Low to High', value: 'engineLeast' },
    { label: 'ResumeModified: New to Old', value: 'modifiedLatest' },
    { label: 'ResumeModified: Old to New', value: 'modifiedOldest' },
    { label: 'Last seen: New to Old', value: 'lastSeenLatest' },
    { label: 'last seen: Old to New', value: 'lastSeenOldest' },
  ],
  CW_JOBS: [
    { label: 'Relevance: Most', value: 'relevancy' },
    { label: 'Last Active: Most Recent', value: 'activityDate' },
    { label: 'Resume Updated: Most Recent', value: 'dateResumeLastUpdated' },
  ],
  JOB_SITE: [
    { label: 'Relevance: Most', value: 'relevancy' },
    { label: 'Last Active: Most Recent', value: 'activityDate' },
    { label: 'Resume Updated: Most Recent', value: 'dateResumeLastUpdated' },
  ],
  JOB_SERVE: [
    { label: 'Best Match', value: 'Best Match' },
    { label: 'Most Recent', value: 'Most Recent' },
    { label: 'Distance', value: 'Distance' },
  ],
  CV_LIBRARY: [
    { label: 'Smart Match', value: 'smartmatch' },
    { label: 'Distance', value: 'distance' },
    { label: 'Last Updated', value: 'updated' },
  ],
};

const countryOptions = {
  JOB_SERVE: [
    { label: 'UKEur', value: 'UKEur' },
    { label: 'Americas', value: 'Americas' },
    { label: 'AusAsia', value: 'AusAsia' },
    { label: 'Canada', value: 'Canada' },
  ],
};

const distanceOptions = {
  UKEur: [
    { label: '0 miles', value: '0 miles' },
    { label: '5 miles', value: '5 miles' },
    { label: '10 miles', value: '10 miles' },
    { label: '15 miles', value: '15 miles' },
    { label: '25 miles', value: '25 miles' },
    { label: '50 miles', value: '50 miles' },
    { label: '75 miles', value: '75 miles' },
    { label: '100 miles', value: '100 miles' },
    { label: '150 miles', value: '150 miles' },
    { label: '200 miles', value: '200 miles' },
  ],
  Americas: [
    { label: '0 miles', value: '0 miles' },
    { label: '5 miles', value: '5 miles' },
    { label: '10 miles', value: '10 miles' },
    { label: '15 miles', value: '15 miles' },
    { label: '25 miles', value: '25 miles' },
    { label: '50 miles', value: '50 miles' },
    { label: '75 miles', value: '75 miles' },
    { label: '100 miles', value: '100 miles' },
    { label: '150 miles', value: '150 miles' },
    { label: '200 miles', value: '200 miles' },
  ],
  Canada: [
    { label: '0 km', value: '0 km' },
    { label: '5 km', value: '5 km' },
    { label: '10 km', value: '10 km' },
    { label: '15 km', value: '15 km' },
    { label: '25 km', value: '25 km' },
    { label: '50 km', value: '50 km' },
    { label: '75 km', value: '75 km' },
    { label: '100 km', value: '100 km' },
    { label: '150 km', value: '150 km' },
    { label: '200 km', value: '200 km' },
  ],
  AusAsia: [
    { label: '0 km', value: '0 km' },
    { label: '5 km', value: '5 km' },
    { label: '10 km', value: '10 km' },
    { label: '15 km', value: '15 km' },
    { label: '25 km', value: '25 km' },
    { label: '50 km', value: '50 km' },
    { label: '75 km', value: '75 km' },
    { label: '100 km', value: '100 km' },
    { label: '150 km', value: '150 km' },
    { label: '200 km', value: '200 km' },
  ],
  CAREER_BUILDER: [
    {
      label: '20 mi',
      value: 20,
    },
    {
      label: '30 mi',
      value: 30,
    },
    {
      label: '50 mi',
      value: 50,
    },
    {
      label: '100 mi',
      value: 100,
    },
    {
      label: '200 mi',
      value: 200,
    },
    {
      label: '500 mi',
      value: 500,
    },
    {
      label: '1000 mi',
      value: 1000,
    },
  ],
};
const toggleOption: WsToggleObject[] = [
  {
    label: 'Search by City',
    value: 'zip',
  },
  {
    label: 'Search by State',
    value: 'location',
  },
];

const cbToggleOption: WsToggleObject[] = [
  {
    label: 'Search by Location',
    value: 'location',
  },
  {
    label: 'Search by ZipCode',
    value: 'zip',
  },
];

const toggleOptionNonUS: WsToggleObject[] = [
  {
    label: 'Search by State',
    value: 'location',
  },
  {
    label: 'Search by Country',
    value: 'country',
  },
];

const careerBuilderEdgeLastActiveFilterByOptions: WsSelectObj[] = [
  {
    value: ' ResumeModified',
    label: 'Resume Modified In',
  },
  {
    value: ' LatestSeenEvidence',
    label: 'Latest Evidence In',
  },
];

const monsterSearchOptions: WsToggleObject[] = [
  {
    label: 'Power Search',
    value: 'Semantic',
  },
  {
    label: 'Classic Search',
    value: 'Boolean',
  },
];
export const psOptions: WsRadioObj[] = [
  {
    value: 'Boolean',
    label: 'Boolean',
  },
  {
    value: 'Semantic',
    label: 'Semantic',
  },
];

const NativeSearchForm = <T,>({
  jobBoardName,
  performSearch,
  openAdvanceFilter,
  country = 'UKEur',
  handleSourceBotBtnClick,
  onViewCvClick,
}: NativeSearchFormProps<T>) => {
  const [params] = useSearchParams();
  const [job] = useJob(params.get('jobId') ?? '');

  const [viewChecker, setViewChecker] = useState<IViewProps[]>([]);
  const [firstSearch, setFirstSearch] = useState<boolean>(false);
  const [jobBoardAccounts, setJobBoardAccounts] = useState<WsSelectObj[]>([]);
  const [openBotDrawer, setOpenBotDrawer] = useState<boolean>(false);
  const [openBotDownloadMatchDrawer, setOpenBotDownloadMatchDrawer] = useState<boolean>(false);
  const [type, setType] = useState<string>();
  const [harvestId, setHarvestId] = useState<string>();
  const workSpace = useWorkspaceContext();

  const setNotification = useNotificationStore((state) => state.setNotification);
  const handleCloseBotDrawer = () => setOpenBotDrawer(false);
  const handleOpenBotDrawer = () => setOpenBotDrawer(true);
  const handleOpenBotDownloadMatchDrawer = (type, harvestId) => {
    setHarvestId(harvestId);
    setType(type);
    setOpenBotDownloadMatchDrawer(true);
  };

  const setUpdateJBViews = useConversationWidgetStore((state) => state.setUpdateJBViews);
  const updateJBViews = useConversationWidgetStore((state) => state.updateJBViews);
  const methods = useFormContext();
  const [token, setToken] = useState<string>('');
  const [auth, setAuth] = useState<string>('');
  const jobBoardAmplitudeEvent = {
    ['DICE_SEARCH']: (value) =>
      firingAmplitudeEvents(`TalentPool_Dice${value}`, 'Dice Search', workSpace, 'Candidates'),

    ['TECH_FETCH']: (value) =>
      firingAmplitudeEvents(
        `Talentpool_TechFetch_Search_${value}`,
        'TechFetch Search',
        workSpace,
        'Candidates'
      ),

    ['MONSTER']: (value) =>
      firingAmplitudeEvents(
        `Talentpool_Monster${value}`,
        'Monster Search',
        workSpace,
        'Candidates'
      ),

    ['CAREER_BUILDER']: (value) =>
      firingAmplitudeEvents(
        `Talentpool_CareerBuilder${value}`,
        'Career Builder Search',
        workSpace,
        'Candidates'
      ),

    ['CAREER_BUILDER_EDGE']: (value) =>
      firingAmplitudeEvents(
        `Talentpool_CareerBuilderEdge${value}`,
        'CareerBuilder Edge Search',
        workSpace,
        'Candidates'
      ),

    ['CV_LIBRARY']: (value) =>
      firingAmplitudeEvents(
        `Talentpool_CV Library${value}`,
        'CV Library Search',
        workSpace,
        'Candidates'
      ),

    ['JOB_SERVE']: (value) =>
      firingAmplitudeEvents(
        `Talentpool_Job Serve${value}`,
        'Job Serve Search',
        workSpace,
        'Candidates'
      ),
    ['JOB_SITE']: (value) =>
      firingAmplitudeEvents(
        `Talentpool_Job Site${value}`,
        'Job Site Search',
        workSpace,
        'Candidates'
      ),
    ['|CW_JOBS']: (value) =>
      firingAmplitudeEvents(
        `Talentpool_CW Jobs${value}`,
        'CW Jobs Search',
        workSpace,
        'Candidates'
      ),
  };

  const { setValue, handleSubmit, watch, getValues } = methods;
  const [searchValue, setSearchValue] = useState('zip');
  const setSearchCriteria = (input: string) => {
    setSearchValue(input);
    setValue('searchCriteria', input);
  };

  const searchQuery = useKeyWordGenerator(Mode.NATIVE, job, jobBoardName);
  useEffect(() => setValue('query', searchQuery), [searchQuery, setValue]);
  useEffect(() => {
    if (updateJBViews) {
      const updatedAccounts = [...jobBoardAccounts];
      updatedAccounts.map((accountData) => {
        if (accountData.value === getValues('accountId')) {
          const updatedLabel = accountData.label.split(' ');
          const remainingViews = updatedLabel[1];
          if (remainingViews) {
            const views = Number(remainingViews.replaceAll('(', ''));
            const updatedViews = views > 0 ? views - 1 : views;
            updatedLabel[1] = `(${updatedViews}`;
          }
          accountData.label = updatedLabel.toString().replaceAll(',', ' ');
        }
      });
      setJobBoardAccounts(updatedAccounts);
      setUpdateJBViews(false);
    }
  }, [getValues, jobBoardAccounts, setUpdateJBViews, updateJBViews]);
  useEffect(() => {
    const getAccounts = async () => {
      try {
        const accounts = await getNativeSearchAccountsForJobBoard(
          jobBoardName,
          params.get('loc') ?? ''
        );
        const viewData = accounts.map((account) => {
          return {
            accountId: account?.id,
            accountUsage: account?.usage?.viewsRemaining,
            userName: account.username,
          };
        });
        setViewChecker(viewData);
        if (jobBoardName === 'MONSTER') {
          setJobBoardAccounts(
            accounts.map((account) => ({
              label: `${account.username} (${account.usage?.viewsRemaining ?? 0} of ${
                account.usage?.quota ?? 0
              } views left )`,
              value: account.id + '#' + account.username,
            }))
          );
        } else {
          setJobBoardAccounts(
            accounts.map((account) => ({
              label: `${account.username} (${account.usage?.viewsRemaining ?? 0} of ${
                account.usage?.quota ?? 0
              } views left )`,
              value: account.id,
            }))
          );
        }

        if (accounts && accounts.length) {
          if (accounts.length > 0) {
            let accountToUse = accounts.find((account) => account.usage?.viewsRemaining ?? 0 > 0);
            accountToUse || jobBoardName === 'JOB_SERVE'
              ? {}
              : setNotification(
                  failed(
                    jobBoardName === 'DICE_SEARCH'
                      ? 'All accounts have exhausted views'
                      : 'Allocated accounts do not have enough credits to view new profiles, but still you can search and view existing profiles'
                  )
                );
            accountToUse = accountToUse ? accountToUse : accounts[0];
            setValue(
              'accountId',
              jobBoardName === 'MONSTER'
                ? accountToUse.id + '#' + accountToUse.username
                : accountToUse.id
            );
            setValue('userName', accountToUse.username);
          } else {
            setNotification(failed('No accounts available'));
            setValue('accountId', 'none');
          }
        } else {
          setNotification(failed('No accounts available'));
          setValue('accountId', 'none');
        }
      } catch (e) {
        setNotification(failed('Request failed with status code 404'));
        setValue('accountId', 'none');
      }
    };
    getAccounts();
  }, [jobBoardName, setViewChecker, setValue, setNotification, searchValue, params]);
  const setSearchQuery = useConversationWidgetStore((state) => state.setSearchQuery);
  const OnSearch = (data) => {
    methods.setValue('page', 1);
    data.page = 1;
    jobBoardAmplitudeEvent[jobBoardName] && jobBoardAmplitudeEvent[jobBoardName]('_Search');
    let jobTitles = [];
    let skills = [];
    if (jobBoardName === 'MONSTER') {
      jobTitles = methods.getValues('jobTitles') ?? [];
      skills = methods.getValues('skills') ?? [];
    }
    const query = `${methods.getValues('query')} ${
      jobTitles?.map((title) => JSON.stringify(title))?.join(' ') ?? ''
    } ${skills?.map((title) => JSON.stringify(title))?.join(' ') ?? ''}`;
    setSearchQuery(query ?? '');
    if (!isEmpty(data.accountId) && data.accountId !== 'none') performSearch(data);
    else setNotification(failed('Please add an account to search'));
  };

  const getOptions = () => {
    const userName = methods.watch('userName');
    let options = toggleOption;
    if (userName) {
      const country = (userName.substring(userName.indexOf('(')).split('_')[1] ?? '')
        .replace(')', '')
        .trim();
      if (country != 'us') {
        options = toggleOptionNonUS;
      }
    }
    return options;
  };

  const getSearchValueOptions = () => {
    const userName = methods.watch('userName');
    let options = searchValue;
    if (searchValue && userName) {
      const country = (userName.substring(userName.indexOf('(')).split('_')[1] ?? '')
        .replace(')', '')
        .trim();
      if (country == 'us') {
        if (searchValue == 'location') {
          options = 'location';
        } else {
          options = 'zip';
        }
      }
      if (country == 'europe') {
        if (searchValue == 'location' || searchValue == 'zip') {
          // For EU Zip - it's city search
          options = 'location';
        } else {
          options = 'country';
        }
      }
    }
    return options;
  };

  const getSearchValueOptionsPlaceHolder = () => {
    const userName = methods.watch('userName');
    let options;
    if (searchValue && userName) {
      const country = (userName.substring(userName.indexOf('(')).split('_')[1] ?? '')
        .replace(')', '')
        .trim();
      if (country == 'us') {
        if (searchValue == 'location') {
          options = 'Search by State';
        } else {
          options = 'Search by City';
        }
      }
      if (country == 'europe') {
        if (searchValue == 'location' || searchValue == 'zip') {
          options = 'Search by State';
        } else {
          options = 'Search by Country/ZipCode';
        }
      }
    }
    return options;
  };
  useEffect(() => {
    const getToken = async () => {
      const res = await csrfTokenCall();
      const token = res.config.headers?.['X-XSRF-TOKEN'];
      const auth = res.config.headers?.['Authorization'];
      setToken(token ?? '');
      setAuth(auth ?? '');
      return token;
    };
    getToken();
  }, []);

  return (
    <FormProvider {...methods}>
      <Grid
        component="form"
        container
        bgcolor={WsColors.White}
        borderRadius={1}
        my={2}
        pr={2}
        pb={2}
        spacing={2}
        onSubmit={handleSubmit(OnSearch as SubmitHandler<FieldValues>)}
      >
        {jobBoardName === 'MONSTER' && params.get('loc')?.includes('us') && (
          <Grid item xs={12}>
            <Grid item xs={12}>
              <WsToggleButton
                buttonsList={monsterSearchOptions}
                selectedIndexValue={(value) => {
                  setValue('locations', []);
                  if (typeof value !== 'number') setValue('searchType', value);
                }}
                defaultValue={methods.getValues('searchType')}
              />
            </Grid>
          </Grid>
        )}
        {/* {watch('searchType') === 'Semantic' && (
          <Grid item xs={12}>
            <WsRadioGroup name="queryType" selectArray={psOptions} size="medium" />
          </Grid>
        )} */}
        {watch('searchType') !== 'Semantic' || watch('queryType') !== 'Semantic' ? (
          <Grid item xs={10}>
            <Card sx={{ p: 0.5 }}>
              <SearchBar
                name="query"
                placeholder="Search for Company, Skills, Job Title, Location"
              />
            </Card>
          </Grid>
        ) : (
          <>
            <Grid item xs={5}>
              <WsAutocompleteInput
                name="jobTitles"
                placeholder="Search for Candidate with job titles"
                title="Job Title"
                options={[]}
                freeSolo={true}
                multiple={true}
                label="Job Title"
              />
            </Grid>
            <Grid item xs={5}>
              <WsSkillsFilter
                name="skills"
                placeHolder="Search for candidate with skill"
                title="skills"
              />
            </Grid>
          </>
        )}

        <Grid
          item
          xs={2}
          mt={watch('searchType') !== 'Semantic' || watch('queryType') !== 'Semantic' ? 0 : 3.5}
        >
          <WsButton
            fullWidth
            variant="outlined"
            size="large"
            type="submit"
            startIcon={<SearchIcon />}
            onClick={() => setFirstSearch(true)}
          >
            Search
          </WsButton>
        </Grid>

        {jobBoardName === 'MONSTER' && watch('searchType') !== 'Semantic' && (
          <Grid item xs={12}>
            {searchValue && (
              <Grid item xs={12}>
                <WsToggleButton
                  buttonsList={getOptions()}
                  selectedIndexValue={(value) => {
                    if (typeof value !== 'number') setSearchCriteria(value);
                  }}
                  defaultValue={getSearchValueOptions()}
                />
              </Grid>
            )}

            <WsLocationAndZipSearch
              distanceSelectName="distance"
              placeHolder={getSearchValueOptionsPlaceHolder()}
              size="small"
              showDistance={searchValue === 'zip' || searchValue === 'country' ? true : false}
              name="locations"
              multiSelect={true}
              searchCriteria={getSearchValueOptions()}
              account={watch('accountId')}
              jobBoard={jobBoardName}
            />
          </Grid>
        )}
        {(jobBoardName === 'CV_LIBRARY' ||
          jobBoardName === 'CW_JOBS' ||
          jobBoardName === 'JOB_SITE') && (
          <Grid item xs={12}>
            <WsLocationAndZipSearch
              distanceSelectName="distance"
              placeHolder={'Search for Location'}
              size="small"
              showDistance={jobBoardName === 'CW_JOBS'}
              name="locations"
              multiSelect={true}
              searchCriteria={searchValue}
              jobBoard={jobBoardName}
            />
          </Grid>
        )}
        {jobBoardName === 'CAREER_BUILDER' && (
          <Grid item xs={12}>
            <Grid item xs={12}>
              <WsToggleButton
                buttonsList={cbToggleOption}
                selectedIndexValue={(value) => {
                  setValue('locations', []);
                  if (typeof value !== 'number') setValue('searchType', value);
                }}
                defaultValue={methods.getValues('searchType')}
              />
            </Grid>
          </Grid>
        )}
        {jobBoardName === 'CAREER_BUILDER' && watch('searchType') === 'zip' && (
          <>
            <Grid item xs={9}>
              <ControlledTextInput
                name={'zipcode'}
                fullWidth
                id={`zipcode-field`}
                placeholder={'eg 1234'}
                variant="outlined"
                size="small"
                labelText="Zip Code"
              />
            </Grid>
            <Grid item xs={3}>
              <WsSelect
                name={'distance'}
                selectArray={distanceOptions.CAREER_BUILDER}
                label="Radius"
                size="small"
                defaultValue="100"
              />
            </Grid>
          </>
        )}
        {jobBoardName !== 'TECH_FETCH' &&
          (jobBoardName !== 'MONSTER' || watch('searchType') === 'Semantic') &&
          jobBoardName !== 'JOB_SERVE' &&
          jobBoardName !== 'CV_LIBRARY' &&
          jobBoardName !== 'CW_JOBS' &&
          jobBoardName !== 'JOB_SITE' &&
          (jobBoardName !== 'CAREER_BUILDER' || watch('searchType') === 'location') && (
            <Grid item xs={12}>
              <WsLocationSearch
                distanceSelectName="distance"
                placeHolder="Search for Location"
                size="small"
                showDistance={true}
                name="locations"
                multiSelect={true}
                wisestepApi={true}
                token={token}
                authorization={auth}
              />
            </Grid>
          )}
        {jobBoardName === 'TECH_FETCH' && (
          <Grid item xs={12}>
            <TagsInput
              placeholder="Add Locations"
              multiple={true}
              initialValue={[]}
              optionsInStringArray={Object.keys(States)}
              onTagsChange={(tags: string[]) => console.log(tags)}
              name="location"
              size="small"
            />
          </Grid>
        )}
        {jobBoardName === 'JOB_SERVE' && (
          <>
            <Grid item xs={9}>
              <ControlledTextInput
                name="locationString"
                fullWidth
                id={`location-field`}
                placeholder="Location"
                variant="outlined"
                size="small"
              />
            </Grid>
            <Grid item xs={3}>
              <WsSelect
                placeholder="Select Distance"
                name="distance"
                selectArray={distanceOptions[country]}
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <WsSelect
                placeholder="Select Country"
                name="countrySelect"
                selectArray={countryOptions[jobBoardName]}
                size="small"
              />
            </Grid>
          </>
        )}

        {jobBoardName === 'MONSTER' && (
          <>
            <Grid item xs={9}>
              <WsSelect
                name="accountId"
                selectArray={
                  !isEmpty(jobBoardAccounts)
                    ? jobBoardAccounts
                    : [{ label: 'No accounts available', value: 'none' }]
                }
                label="Account"
                size="small"
              />
            </Grid>
          </>
        )}
        {jobBoardName !== 'MONSTER' && jobBoardName != 'CAREER_BUILDER_EDGE' && (
          <>
            <Grid item xs={9}>
              <WsSelect
                name="accountId"
                selectArray={
                  !isEmpty(jobBoardAccounts)
                    ? jobBoardAccounts
                    : [{ label: 'No accounts available', value: 'none' }]
                }
                label="Account"
                size="small"
              />
            </Grid>
          </>
        )}
        {jobBoardName == 'CAREER_BUILDER_EDGE' && (
          <>
            <Grid item xs={6}>
              <WsSelect
                name="accountId"
                selectArray={
                  !isEmpty(jobBoardAccounts)
                    ? jobBoardAccounts
                    : [{ label: 'No accounts available', value: 'none' }]
                }
                label="Account"
                size="small"
              />
            </Grid>
            <Grid item xs={3}>
              <WsSelect
                name="filterBy"
                selectArray={careerBuilderEdgeLastActiveFilterByOptions}
                size="small"
                label="&nbsp;"
                defaultValue=" ResumeModified"
              />
            </Grid>
            <Grid item xs={3}>
              <WsSelect
                name="lastActive"
                selectArray={lastActiveValues[jobBoardName]}
                label="&nbsp;"
                size="small"
                defaultValue="[NOW-30DAYS/DAY TO *]"
              />
            </Grid>
          </>
        )}

        {jobBoardName != 'CAREER_BUILDER_EDGE' && (
          <Grid item xs={3}>
            <WsSelect
              name="lastActive"
              selectArray={lastActiveValues[jobBoardName]}
              label="Last Active"
              size="small"
              displayEmpty={true}
            />
          </Grid>
        )}
        {workSpace.id !== '0ed56aea-54c9-4485-987f-ffb35872eb38' && (
          <Grid item xs={6}>
            <WsButton
              variant="contained"
              size="large"
              onClick={() => {
                jobBoardAmplitudeEvent[jobBoardName]('_Sourcing_Bots');
                handleSourceBotBtnClick && handleSourceBotBtnClick();
              }}
            >
              Sourcing Bots
            </WsButton>
            <Link
              href="#"
              color="primary"
              underline="none"
              ml={2}
              onClick={() => {
                jobBoardAmplitudeEvent[jobBoardName]('_Active_Sourcing_Bots');
                handleOpenBotDrawer();
              }}
            >
              <CgBot /> Active Sourcing Bots
            </Link>
          </Grid>
        )}
        <Grid xs={6} item container spacing={2}>
          <Grid item xs={7}>
            {watch('searchType') !== 'Semantic' && (
              <WsSelect name="sort" selectArray={sortOptions[jobBoardName]} size="small" />
            )}
          </Grid>
          {!location.pathname.includes('native-search-new') && (
            <Grid container justifyContent="flex-end" item xs={5}>
              <WsButton
                variant="outlined"
                onClick={() => openAdvanceFilter && openAdvanceFilter(true)}
                size="large"
                type="button"
                startIcon={<BiFilterAlt />}
              >
                Advanced Filters
              </WsButton>
            </Grid>
          )}
        </Grid>
      </Grid>
      {isEmpty(viewChecker) && (
        <Grid sx={{ ml: -2 }}>
          <NoDataFoundCard
            text={`No ${toLowerCaseWithFirstLetterCapital(
              jobBoardName
            )} Account Added, Contact workspace admin to Add or Manage account
            `}
            subElement={<AddAccountButton url={getJobBoardUrl(jobBoardName)} />}
          />
        </Grid>
      )}
      {!isEmpty(viewChecker) && !firstSearch && (
        <Grid sx={{ ml: -2 }}>
          <NoDataFoundCard text="Apply search query to find your candidates." />
        </Grid>
      )}
      <WsDrawer
        size="full"
        title="Sourcing Bots"
        iconType="cross"
        open={openBotDrawer}
        onClose={handleCloseBotDrawer}
        style={{ zIndex: '1300' }}
      >
        <HarvestBotsList handleOpenBotDownloadMatchDrawer={handleOpenBotDownloadMatchDrawer} />
      </WsDrawer>
      <WsDrawer
        size="large"
        title={'Informatica'}
        iconType="cross"
        open={openBotDownloadMatchDrawer}
        onClose={() => setOpenBotDownloadMatchDrawer(false)}
      >
        <HarvestBotDownloadMatchList
          onViewCvClick={onViewCvClick}
          type={type}
          harvestId={harvestId}
          jobBoardName={jobBoardName}
        />
      </WsDrawer>
    </FormProvider>
  );
};

export default NativeSearchForm;
